import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { Badge } from "@/components/ui/badge";
import type { BadgeProps } from "@/components/ui/badge";
import { CheckboxField, NumberField, SelectField, TextField } from "@/components/admin/fields";
import {
  createVehicleType,
  getVehicleType,
  listVehicleTypes,
  updateVehicleType,
  type VehicleType,
  type VehicleTypePayload,
} from "@/services/api/loadPlans";

const BODY_TYPES = ["curtainsider", "box", "flatbed", "refrigerated", "other"];
const VEHICLE_CLASSES = ["trailer", "rigid"];

interface VehicleTypeForm {
  code: string;
  name: string;
  vehicle_class: string;
  body_type: string;
  internal_length_mm: string;
  internal_width_mm: string;
  internal_height_mm: string;
  is_double_deck: boolean;
  deck2_clearance_mm: string;
  max_payload_kg: string;
  nominal_pallet_count: string;
  loads_from_rear: boolean;
  loads_from_left: boolean;
  loads_from_right: boolean;
  is_active: boolean;
  sort_order: string;
}

const emptyForm: VehicleTypeForm = {
  code: "",
  name: "",
  vehicle_class: "trailer",
  body_type: "curtainsider",
  internal_length_mm: "13600",
  internal_width_mm: "2450",
  internal_height_mm: "2600",
  is_double_deck: false,
  deck2_clearance_mm: "",
  max_payload_kg: "",
  nominal_pallet_count: "",
  loads_from_rear: true,
  loads_from_left: false,
  loads_from_right: false,
  is_active: true,
  sort_order: "0",
};

const toPayload = (form: VehicleTypeForm): VehicleTypePayload => ({
  code: form.code.trim(),
  name: form.name.trim(),
  vehicle_class: form.vehicle_class,
  body_type: form.body_type,
  internal_length_mm: Number(form.internal_length_mm) || 0,
  internal_width_mm: Number(form.internal_width_mm) || 0,
  internal_height_mm: Number(form.internal_height_mm) || 0,
  is_double_deck: form.is_double_deck,
  deck2_clearance_mm:
    form.is_double_deck && form.deck2_clearance_mm ? Number(form.deck2_clearance_mm) : null,
  max_payload_kg: form.max_payload_kg ? Number(form.max_payload_kg) : null,
  nominal_pallet_count: form.nominal_pallet_count ? Number(form.nominal_pallet_count) : null,
  loads_from_rear: form.loads_from_rear,
  loads_from_left: form.loads_from_left,
  loads_from_right: form.loads_from_right,
  is_active: form.is_active,
  sort_order: Number(form.sort_order) || 0,
});

/** Mini top-view outline of the load space, drawn from the dimensions. */
export function VehicleTypePreview({
  vehicle,
}: {
  vehicle: { internal_length_mm: number; internal_width_mm: number; is_double_deck: boolean };
}) {
  const w = 72;
  const h =
    vehicle.internal_length_mm > 0
      ? Math.max(14, Math.min(72, (vehicle.internal_width_mm / vehicle.internal_length_mm) * 160))
      : 24;
  return (
    <svg width={w + 12} height={h + 12} aria-hidden className="shrink-0">
      <rect
        x={6}
        y={6}
        width={w}
        height={h}
        rx={3}
        className="fill-muted/40 stroke-muted-foreground"
        strokeWidth={1.5}
      />
      {vehicle.is_double_deck && (
        <line
          x1={6}
          y1={6 + h / 3}
          x2={6 + w}
          y2={6 + h / 3}
          className="stroke-muted-foreground"
          strokeDasharray="3 2"
        />
      )}
    </svg>
  );
}

/** Vehicle/trailer type reference data: reusable sizes the planner plans into. */
export function VehicleTypesPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const query = useQuery({
    queryKey: ["vehicle-types", offset],
    queryFn: () => listVehicleTypes(true),
  });
  const rows: VehicleType[] = query.data ?? [];

  const columns: ColumnDef<VehicleType>[] = [
    {
      id: "preview",
      header: "",
      cell: ({ row }) => <VehicleTypePreview vehicle={row.original} />,
    },
    { accessorKey: "code", header: t("admin.vehicleTypeCode") },
    { accessorKey: "name", header: t("admin.name") },
    {
      id: "dims",
      header: t("admin.dimensions"),
      cell: ({ row }) =>
        `${row.original.internal_length_mm}×${row.original.internal_width_mm}×${row.original.internal_height_mm}`,
    },
    { accessorKey: "body_type", header: t("admin.bodyType") },
    { accessorKey: "vehicle_class", header: t("admin.vehicleClass") },
    {
      accessorKey: "is_double_deck",
      header: t("admin.doubleDeck"),
      cell: ({ getValue }) =>
        getValue() ? <Badge variant="info">{t("admin.doubleDeck")}</Badge> : "—",
    },
    {
      accessorKey: "is_active",
      header: t("admin.active"),
      cell: ({ row }) => (
        <Badge
          variant={(row.original.is_active ? "success" : "secondary") as BadgeProps["variant"]}
        >
          {row.original.is_active ? t("common.yes") : t("common.no")}
        </Badge>
      ),
    },
  ];

  const sideLabel = (v: VehicleType) =>
    [
      v.loads_from_rear ? t("admin.sideRear") : null,
      v.loads_from_left ? t("admin.sideLeft") : null,
      v.loads_from_right ? t("admin.sideRight") : null,
    ]
      .filter(Boolean)
      .join(" + ") || "—";

  const toForm = (r: VehicleType): VehicleTypeForm => ({
    code: r.code,
    name: r.name,
    vehicle_class: r.vehicle_class,
    body_type: r.body_type,
    internal_length_mm: String(r.internal_length_mm),
    internal_width_mm: String(r.internal_width_mm),
    internal_height_mm: String(r.internal_height_mm),
    is_double_deck: r.is_double_deck,
    deck2_clearance_mm: r.deck2_clearance_mm === null ? "" : String(r.deck2_clearance_mm),
    max_payload_kg: r.max_payload_kg === null ? "" : String(r.max_payload_kg),
    nominal_pallet_count: r.nominal_pallet_count === null ? "" : String(r.nominal_pallet_count),
    loads_from_rear: r.loads_from_rear,
    loads_from_left: r.loads_from_left,
    loads_from_right: r.loads_from_right,
    is_active: r.is_active,
    sort_order: String(r.sort_order),
  });

  return (
    <FullCrudPage<VehicleType, VehicleTypeForm>
      title={t("nav.vehicleTypes")}
      entityType="vehicle_type"
      getRecord={getVehicleType}
      columns={columns}
      data={rows.slice(offset, offset + limit)}
      total={rows.length}
      isLoading={query.isLoading}
      limit={limit}
      offset={offset}
      onPageChange={setOffset}
      queryKey="vehicle-types"
      emptyForm={emptyForm}
      toForm={toForm}
      canSubmit={(f) =>
        Boolean(
          f.code.trim() &&
            f.name.trim() &&
            Number(f.internal_length_mm) > 0 &&
            Number(f.internal_width_mm) > 0 &&
            Number(f.internal_height_mm) > 0 &&
            (!f.is_double_deck || Number(f.deck2_clearance_mm) > 0),
        )
      }
      renderForm={(form, setForm) => (
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("admin.vehicleTypeCode")}
            value={form.code}
            onChange={(v) => setForm({ ...form, code: v })}
            placeholder="STD-ARTIC-1360"
            required
          />
          <TextField
            label={t("admin.name")}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <SelectField
            label={t("admin.vehicleClass")}
            value={form.vehicle_class}
            onChange={(v) => setForm({ ...form, vehicle_class: v })}
            options={VEHICLE_CLASSES.map((c) => ({ value: c, label: t(`admin.class.${c}`) }))}
          />
          <SelectField
            label={t("admin.bodyType")}
            value={form.body_type}
            onChange={(v) => setForm({ ...form, body_type: v })}
            options={BODY_TYPES.map((b) => ({ value: b, label: t(`admin.body.${b}`) }))}
          />
          <NumberField
            label={t("admin.internalLengthMm")}
            value={form.internal_length_mm}
            onChange={(v) => setForm({ ...form, internal_length_mm: v })}
          />
          <NumberField
            label={t("admin.internalWidthMm")}
            value={form.internal_width_mm}
            onChange={(v) => setForm({ ...form, internal_width_mm: v })}
          />
          <NumberField
            label={t("admin.internalHeightMm")}
            value={form.internal_height_mm}
            onChange={(v) => setForm({ ...form, internal_height_mm: v })}
          />
          <NumberField
            label={t("admin.maxPayloadKg")}
            value={form.max_payload_kg}
            onChange={(v) => setForm({ ...form, max_payload_kg: v })}
          />
          <NumberField
            label={t("admin.nominalPalletCount")}
            value={form.nominal_pallet_count}
            onChange={(v) => setForm({ ...form, nominal_pallet_count: v })}
          />
          <NumberField
            label={t("admin.sortOrder")}
            value={form.sort_order}
            onChange={(v) => setForm({ ...form, sort_order: v })}
          />
          <CheckboxField
            label={t("admin.doubleDeck")}
            checked={form.is_double_deck}
            onChange={(v) => setForm({ ...form, is_double_deck: v })}
          />
          {form.is_double_deck && (
            <NumberField
              label={t("admin.deck2ClearanceMm")}
              value={form.deck2_clearance_mm}
              onChange={(v) => setForm({ ...form, deck2_clearance_mm: v })}
            />
          )}
          <CheckboxField
            label={t("admin.sideRear")}
            checked={form.loads_from_rear}
            onChange={(v) => setForm({ ...form, loads_from_rear: v })}
          />
          <CheckboxField
            label={t("admin.sideLeft")}
            checked={form.loads_from_left}
            onChange={(v) => setForm({ ...form, loads_from_left: v })}
          />
          <CheckboxField
            label={t("admin.sideRight")}
            checked={form.loads_from_right}
            onChange={(v) => setForm({ ...form, loads_from_right: v })}
          />
          <CheckboxField
            label={t("admin.active")}
            checked={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: v })}
          />
          <div className="col-span-2 flex items-center gap-3">
            <VehicleTypePreview
              vehicle={{
                internal_length_mm: Number(form.internal_length_mm) || 1,
                internal_width_mm: Number(form.internal_width_mm) || 1,
                is_double_deck: form.is_double_deck,
              }}
            />
            <span className="text-xs text-muted-foreground">{t("admin.vehiclePreviewHint")}</span>
          </div>
        </div>
      )}
      onSave={async (mode, form, record) =>
        mode === "edit" && record
          ? updateVehicleType(record.id, toPayload(form))
          : createVehicleType(toPayload(form))
      }
      onDelete={async () => undefined}
      canDelete={() => false}
      recordTitle={(r) => r.code}
      detailFields={(r) => [
        { label: t("admin.vehicleTypeCode"), value: r.code, field: "code" },
        { label: t("admin.name"), value: r.name, field: "name" },
        {
          label: t("admin.dimensions"),
          value: `${r.internal_length_mm}×${r.internal_width_mm}×${r.internal_height_mm} mm`,
        },
        {
          label: t("admin.bodyType"),
          value: t(`admin.body.${r.body_type}`),
          field: "body_type",
        },
        {
          label: t("admin.vehicleClass"),
          value: t(`admin.class.${r.vehicle_class}`),
          field: "vehicle_class",
        },
        { label: t("admin.maxPayloadKg"), value: r.max_payload_kg ?? "—", field: "max_payload_kg" },
        {
          label: t("admin.nominalPalletCount"),
          value: r.nominal_pallet_count ?? "—",
          field: "nominal_pallet_count",
        },
        {
          label: t("admin.doubleDeck"),
          value: r.is_double_deck
            ? `${t("common.yes")} — ${r.deck2_clearance_mm ?? "?"} mm`
            : t("common.no"),
          field: "is_double_deck",
        },
        { label: t("admin.loadingSides"), value: sideLabel(r) },
        { label: t("admin.sortOrder"), value: r.sort_order, field: "sort_order" },
        {
          label: t("admin.active"),
          value: r.is_active ? t("common.yes") : t("common.no"),
          field: "is_active",
        },
      ]}
    />
  );
}

